import { auth } from '../firebase';
import { getDeviceHeaders } from '../utils/deviceInfo';
import { isOfflineAddonActive } from './addonFlag';
import { createOfflineEngine, pending } from './engine';
import { createStorage } from './storage';
import { saveLegacy } from './legacy';

export const API = 'https://api.usenode.com/';
const IDENTITY_KEY = 'nodeOfflineIdentityV1';
const instances = new Map();
export const announce = () => window.dispatchEvent(new Event('node-offline-change'));
export const openOffline = detail => window.dispatchEvent(new CustomEvent('node-offline-open', { detail }));

function readCachedIdentity() {
  try {
    const cached = JSON.parse(localStorage.getItem(IDENTITY_KEY) || 'null');
    if (!cached?.userId || !cached?.businessId) return null;
    if (cached.businessId !== localStorage.getItem('businessId')) return null;
    return { userId: cached.userId, businessId: cached.businessId };
  } catch (_) {
    return null;
  }
}

function rememberIdentity(identity) {
  try {
    localStorage.setItem(IDENTITY_KEY, JSON.stringify({ ...identity, savedAt: Date.now() }));
  } catch (_) {}
}

export function currentIdentity() {
  const userId = auth.currentUser?.uid;
  const businessId = localStorage.getItem('businessId');
  if (localStorage.getItem('sandboxMode') === '1' || localStorage.getItem('emulatedRole')) return null;
  if (!isOfflineAddonActive()) return null;
  if (userId && businessId) {
    const identity = { userId, businessId };
    rememberIdentity(identity);
    return identity;
  }
  return readCachedIdentity();
}
export function getEngine(identity = currentIdentity()) {
  if (!identity) return null;
  const scope = JSON.stringify([API, identity.userId, identity.businessId]);
  if (!instances.has(scope)) {
    const assertIdentity = () => {
      const current = currentIdentity();
      if (current?.userId !== identity.userId || current?.businessId !== identity.businessId) throw new Error('ACCOUNT_CHANGED');
    };
    const transport = async (method, path, body) => {
      assertIdentity();
      if (!auth.currentUser) throw new Error('AUTHENTICATION_REQUIRED');
      const token = await auth.currentUser.getIdToken();
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...getDeviceHeaders(identity.userId) };
      const call = async (url, options) => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 30000);
        try { return await fetch(API + url, { ...options, signal: controller.signal }); }
        finally { clearTimeout(timeout); }
      };
      let businessToken = localStorage.getItem('businessToken');
      for (let attempt = 0; attempt < 2; attempt++) {
        assertIdentity();
        const response = await call(path, { method, headers: { ...headers, businesstoken: businessToken || '' }, ...(body ? { body: JSON.stringify(body) } : {}) });
        const data = await response.json();
        if (response.ok) { assertIdentity(); return data; }
        if (!attempt && (response.status === 401 || response.status === 509)) {
          const minted = await call('users/business-token', { method: 'POST', headers, body: JSON.stringify({ businessId: identity.businessId }) });
          const fresh = await minted.json();
          if (minted.ok && fresh.access?.token) { assertIdentity(); businessToken = fresh.access.token; localStorage.setItem('businessToken', businessToken); continue; }
        }
        const error = new Error(data.error?.code || 'AUTHENTICATION_REQUIRED');
        error.status = response.status; throw error;
      }
    };
    instances.set(scope, createOfflineEngine({ storage: createStorage(scope), transport, automaticRefresh: true,
      uuid: () => crypto.randomUUID(),
      hash: async value => [...new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)))].map(b => b.toString(16).padStart(2, '0')).join(''),
      onChange: announce,
      exclusive: fn => navigator.locks ? navigator.locks.request(`node-offline:${scope}`, { ifAvailable: true }, lock => lock ? fn() : undefined) : fn(),
    }));
  }
  return instances.get(scope);
}
export async function interceptLegacy(method, url, body) {
  if (!/^\/?coreSeqV[56](\/single)?$/.test(url || '')) return null;
  const engine = getEngine();
  if (!engine) return null;
  let result;
  try { result = await saveLegacy(engine, method, url, body || {}); }
  catch (error) {
    if (navigator.onLine !== false && ['ACTION_ONLINE_ONLY', 'FIELD_ONLINE_ONLY', 'RELATION_ONLINE_ONLY', 'OFFLINE_ACCESS_EXPIRED'].includes(error.message)) return null;
    throw error;
  }
  if (result) { openOffline(); engine.sync().catch(() => {}); }
  return result;
}
export async function guardOfflineLogout() {
  const engine = getEngine();
  if (!engine) return true;
  const state = await engine.read();
  if (!pending(state).length) return true;
  // Logging out locks the scope but does not discard it. There is no automatic
  // purge: only removeSelection can abandon work, after explicit confirmation.
  return window.confirm('Des changements ne sont pas encore publiés. Ils resteront sur cet appareil et seront accessibles après reconnexion au même compte. Se déconnecter ?');
}
